import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ProjectsGridProps, ProjectCard } from '../types';
import { ProjectsGridFilterBar } from './ProjectsGridFilters';
import {
  Wrapper,
  Container,
  Grid,
  Card,
  Image,
  Info,
  CardTitle,
  CardAddress,
  CardLink,
  Badge,
  ShowMore,
  Content,
  HoverOverlay,
  CardWrapper,
  GridExpandable,
  CardImageWrapper
} from './styled';
import { usePashaProjectsGridHook } from '../../../hooks/usePashaProjectsGridHook';
import { useOnScreen } from '../../../hooks/useOnScreen';

export const ProjectsGrid: React.FC<ProjectsGridProps> = ({
  landingBrand,
  setProjectseRef,
  setVisibleTabRef
}) => {
  const { filter, setFilter, filtered } =
    usePashaProjectsGridHook(landingBrand);
  const [expanded, setExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number | 'auto'>(0);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentTitleRef = useRef<HTMLDivElement | null>(null);
  const isVisible = useOnScreen(contentTitleRef);

  const { t } = useTranslation();

  useEffect(() => {
    setExpanded(false);
  }, [filter]);

  useEffect(() => {
    setProjectseRef?.(contentTitleRef);
    if (isVisible) return setVisibleTabRef?.(contentTitleRef);
  }, [isVisible]);

  const baseItems = filtered.slice(0, 4);
  const extraItems = filtered.slice(4);
  const isExpandable = extraItems.length > 0;

  useEffect(() => {
    if (!contentRef.current) return;

    const contentEl = contentRef.current;

    if (expanded) {
      const fullHeight = contentEl.scrollHeight;
      setMaxHeight(fullHeight);
      const timeout = setTimeout(() => setMaxHeight('auto'), 400);
      return () => clearTimeout(timeout);
    }
    if (maxHeight === 'auto') {
      const currentHeight = contentEl.scrollHeight;
      setMaxHeight(currentHeight);
      requestAnimationFrame(() => {
        setMaxHeight(0);
      });
    } else {
      setMaxHeight(0);
    }
  }, [expanded, extraItems.length]);

  return (
    <Wrapper ref={contentTitleRef}>
      <Container>
        <Content>
          <ProjectsGridFilterBar
            filter={filter}
            setFilter={setFilter}
            data-cy="pasha-projects-filters"
          />

          <Grid data-cy="pasha-projects-grid">
            {baseItems.map((project: ProjectCard, idx: number) => (
              <CardWrapper
                key={idx}
                data-cy={`pasha-projects-card-wrapper-${idx}`}
              >
                <Card data-cy={`pasha-projects-card-${idx}`}>
                  <CardLink
                    href={project.url}
                    target="_blank"
                    data-cy="pasha-project-card"
                  >
                    <Info>
                      <CardTitle>{project.title}</CardTitle>
                      <CardAddress>
                        {project.address}
                        {project.forSale && (
                          <Badge>{t('pasha.filter_sale')}</Badge>
                        )}
                        {project.forRent && (
                          <Badge>{t('pasha.filter_rent')}</Badge>
                        )}
                      </CardAddress>
                    </Info>
                    <CardImageWrapper>
                      <Image src={project.image} alt={project.title} />
                    </CardImageWrapper>
                    <HoverOverlay />
                  </CardLink>
                </Card>
              </CardWrapper>
            ))}
          </Grid>

          {isExpandable && (
            <GridExpandable
              maxHeight={maxHeight}
              data-cy="pasha-projects-grid-expandable"
            >
              <Grid ref={contentRef}>
                {extraItems.map((project: ProjectCard, idx: number) => (
                  <CardWrapper
                    key={idx}
                    data-cy={`pasha-projects-card-wrapper-extra-${idx}`}
                  >
                    <Card data-cy={`pasha-projects-card-extra-${idx}`}>
                      <CardLink
                        href={project.url}
                        target="_blank"
                        data-cy={`pasha-project-card-link-extra-${idx}`}
                      >
                        <Info>
                          <CardTitle
                            data-cy={`pasha-project-card-title-extra-${idx}`}
                          >
                            {project.title}
                          </CardTitle>
                          <CardAddress
                            data-cy={`pasha-project-card-address-extra-${idx}`}
                          >
                            {project.address}
                            {project.forSale && (
                              <Badge
                                data-cy={`pasha-project-badge-sale-extra-${idx}`}
                              >
                                {t('pasha.filter_sale')}
                              </Badge>
                            )}
                            {project.forRent && (
                              <Badge
                                data-cy={`pasha-project-badge-rent-extra-${idx}`}
                              >
                                {t('pasha.filter_rent')}
                              </Badge>
                            )}
                          </CardAddress>
                        </Info>
                        <CardImageWrapper>
                          <Image src={project.image} alt={project.title} />
                        </CardImageWrapper>
                        <HoverOverlay />
                      </CardLink>
                    </Card>
                  </CardWrapper>
                ))}
              </Grid>
            </GridExpandable>
          )}

          {isExpandable && (
            <ShowMore
              onClick={() => setExpanded(prev => !prev)}
              data-cy="pasha-project-expandable"
              data-stat={`show-${expanded ? 'close' : 'more'}-btn`}
              $expanded={expanded}
            >
              {expanded ? t('common.close') : t('pasha.filter_btn')}
            </ShowMore>
          )}
        </Content>
      </Container>
    </Wrapper>
  );
};
